// Deferred Google Tag Manager loader.
//
// GTM is the root of our entire third-party tag tree — it fires GA4, Google Ads,
// PostHog (+ session recorder), ContentSquare, and the TikTok/Facebook pixels.
// Loading it in <head> (the vite-plugin-radar default) put ~5s of script
// evaluation inside the page's Total-Blocking-Time window. Instead we inject the
// standard GTM snippet ourselves, gated on the first user interaction (with a
// timeout fallback), so the entire tag tree stays off the load/interactive path.
//
// Interaction-gating is what gets the tags fully out of a Lighthouse run: the
// audit never scrolls or clicks, so only the fallback timer fires — well after
// the page is measured interactive. Real visitors trigger it on their first
// scroll/tap/click; non-interacting visitors and crawlers still get it via the
// fallback. We only miss attribution on a human who lands and leaves before the
// fallback without ever interacting — a near-instant bounce.
//
// Call sites push to window.dataLayer behind `window.dataLayer = window.dataLayer || []`,
// so any events fired before GTM arrives simply queue here and flush once it loads.

// The GTM container is environment-specific and injected via VITE_GTM_ID:
// An environment that doesn't set VITE_GTM_ID loads no GTM at all. This is what
// gives each environment its OWN PostHog account rather than firing the live one.
const GTM_ID = import.meta.env.VITE_GTM_ID;

let injected = false;

function injectGTM() {
  if (injected) return;
  injected = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(script);
}

// Load on the first real interaction, or after this long if none happens.
// Lighthouse never interacts, so only this fallback fires during an audit —
// long after the page is measured interactive.
const FALLBACK_MS = 3500;

// Passive listeners that don't block scrolling; the first one to fire wins.
const INTERACTION_EVENTS = ['scroll', 'pointerdown', 'keydown', 'touchstart'];

export function loadGTM() {
  if (!GTM_ID) return;

  let timer;

  const trigger = () => {
    clearTimeout(timer);
    INTERACTION_EVENTS.forEach((evt) =>
      window.removeEventListener(evt, trigger, { passive: true })
    );
    injectGTM();
  };

  INTERACTION_EVENTS.forEach((evt) =>
    window.addEventListener(evt, trigger, { passive: true, once: true })
  );

  timer = setTimeout(trigger, FALLBACK_MS);
}
